/**
 * Secure Storage Utility
 *
 * Provides a more secure way to store sensitive data in the browser.
 * Uses sessionStorage for access tokens (more secure, cleared on tab close)
 * and encrypted localStorage for refresh tokens.
 *
 * This is a browser-based implementation. For production, consider:
 * - HttpOnly cookies for refresh tokens (requires backend support)
 * - Native secure storage for mobile apps (Keychain/KeyStore)
 */

// Simple encryption key derived from browser fingerprint
// Note: This provides obfuscation, not true encryption
// For true security, refresh tokens should be in HttpOnly cookies
const getStorageKey = () => {
  const nav = window.navigator;
  const screen = window.screen;
  const fingerprint = `${nav.userAgent}${nav.language}${screen.height}${screen.width}`;
  return btoa(fingerprint).slice(0, 32);
};

// Simple XOR encryption (obfuscation)
// Uses encodeURIComponent to handle Unicode characters properly
const encrypt = (text) => {
  if (!text) return '';
  try {
    // Encode to handle Unicode characters
    const encodedText = encodeURIComponent(text);
    const key = getStorageKey();
    let result = '';

    for (let i = 0; i < encodedText.length; i++) {
      result += String.fromCharCode(encodedText.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }

    // Convert to hex instead of base64 to avoid Unicode issues
    return Array.from(result)
      .map(char => char.charCodeAt(0).toString(16).padStart(2, '0'))
      .join('');
  } catch (error) {
    console.error('Encryption failed:', error);
    return '';
  }
};

const decrypt = (encoded) => {
  if (!encoded) return '';
  try {
    // Convert from hex back to string
    const hexPairs = encoded.match(/.{1,2}/g) || [];
    const encrypted = hexPairs
      .map(hex => String.fromCharCode(parseInt(hex, 16)))
      .join('');

    const key = getStorageKey();
    let result = '';

    for (let i = 0; i < encrypted.length; i++) {
      result += String.fromCharCode(encrypted.charCodeAt(i) ^ key.charCodeAt(i % key.length));
    }

    // Decode to get original Unicode text
    return decodeURIComponent(result);
  } catch (error) {
    console.error('Decryption failed:', error);
    return '';
  }
};

const secureStorage = {
  // Access Token - хранится в sessionStorage (более безопасно)
  // Удаляется при закрытии вкладки
  setAccessToken: (token) => {
    if (token) {
      sessionStorage.setItem('_at', encrypt(token));
    }
  },

  getAccessToken: () => {
    const encrypted = sessionStorage.getItem('_at');
    return decrypt(encrypted);
  },

  removeAccessToken: () => {
    sessionStorage.removeItem('_at');
  },

  // Refresh Token - хранится в localStorage (зашифрованный)
  // Живет дольше, но зашифрован для защиты
  setRefreshToken: (token) => {
    if (token) {
      localStorage.setItem('_rt', encrypt(token));
    }
  },

  getRefreshToken: () => {
    const encrypted = localStorage.getItem('_rt');
    return decrypt(encrypted);
  },

  removeRefreshToken: () => {
    localStorage.removeItem('_rt');
  },

  // User Data - базовая информация о пользователе
  setUserData: (userData) => {
    if (userData) {
      localStorage.setItem('_ud', encrypt(JSON.stringify(userData)));
    }
  },

  getUserData: () => {
    const encrypted = localStorage.getItem('_ud');
    const decrypted = decrypt(encrypted);
    if (!decrypted) return null;
    try {
      return JSON.parse(decrypted);
    } catch (error) {
      console.error('Failed to parse user data:', error);
      return null;
    }
  },

  removeUserData: () => {
    localStorage.removeItem('_ud');
  },

  // Clear all auth data
  clearAll: () => {
    secureStorage.removeAccessToken();
    secureStorage.removeRefreshToken();
    secureStorage.removeUserData();
    // Очищаем также старые незашифрованные токены (миграция)
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('userData');
    localStorage.removeItem('token');
    localStorage.removeItem('waymorr_current_hosting');
  },

  // Migration from old localStorage keys
  migrate: () => {
    try {
      // Мигрируем старые незашифрованные токены
      const oldAccessToken = localStorage.getItem('access_token');
      const oldRefreshToken = localStorage.getItem('refresh_token');
      const oldUserData = localStorage.getItem('userData');

      if (oldAccessToken) {
        secureStorage.setAccessToken(oldAccessToken);
        localStorage.removeItem('access_token');
      }

      if (oldRefreshToken) {
        secureStorage.setRefreshToken(oldRefreshToken);
        localStorage.removeItem('refresh_token');
      }

      if (oldUserData) {
        try {
          const userData = JSON.parse(oldUserData);
          secureStorage.setUserData(userData);
          localStorage.removeItem('userData');
        } catch (error) {
          console.error('Failed to migrate user data:', error);
        }
      }
    } catch (error) {
      console.error('Migration failed:', error);
    }
  }
};

export default secureStorage;
